import React, { createContext, useContext, useEffect, useRef, useCallback } from 'react';

import {
  ShortcutHandler,
  matchesShortcut,
  shouldPreventShortcut,
} from '../utils/keyboardShortcuts';

interface KeyboardShortcutsContextType {
  registerShortcuts: (handler: ShortcutHandler) => void;
  unregisterShortcuts: (handlerId: string) => void;
  enabled: boolean;
  setEnabled: (enabled: boolean) => void;
}

const KeyboardShortcutsContext = createContext<KeyboardShortcutsContextType | undefined>(
  undefined,
);

export const useKeyboardShortcutsContext = () => {
  const context = useContext(KeyboardShortcutsContext);
  if (!context) {
    throw new Error(
      'useKeyboardShortcutsContext must be used within KeyboardShortcutsProvider',
    );
  }
  return context;
};

interface KeyboardShortcutsProviderProps {
  children: React.ReactNode;
}

export const KeyboardShortcutsProvider: React.FC<KeyboardShortcutsProviderProps> = ({
  children,
}) => {
  const handlersRef = useRef<Map<string, ShortcutHandler>>(new Map());
  const enabledRef = useRef<boolean>(true);

  const registerShortcuts = useCallback((handler: ShortcutHandler) => {
    handlersRef.current.set(handler.id, handler);
  }, []);

  const unregisterShortcuts = useCallback((handlerId: string) => {
    handlersRef.current.delete(handlerId);
  }, []);

  const setEnabled = useCallback((enabled: boolean) => {
    enabledRef.current = enabled;
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // If shortcuts are disabled globally, do nothing
      if (!enabledRef.current) return;

      // Get all registered handlers sorted by priority (highest first)
      const handlers = Array.from(handlersRef.current.values()).sort(
        (a, b) => (b.priority || 0) - (a.priority || 0),
      );

      // Try to find a matching shortcut
      for (const handler of handlers) {
        for (const shortcut of handler.shortcuts) {
          // Skip disabled shortcuts
          if (shortcut.disabled) continue;

          // Check if the event matches this shortcut
          if (matchesShortcut(event, shortcut)) {
            // Check if we should prevent this shortcut (e.g., user is typing)
            if (shouldPreventShortcut(shortcut)) {
              continue;
            }

            // Prevent default browser behavior
            event.preventDefault();
            event.stopPropagation();

            // Execute the shortcut action
            try {
              shortcut.action();
            } catch (error) {
              console.error('Error executing keyboard shortcut:', error);
            }

            // Only execute the first matching shortcut
            return;
          }
        }
      }
    };

    // Add global keydown listener
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const value: KeyboardShortcutsContextType = {
    registerShortcuts,
    unregisterShortcuts,
    enabled: enabledRef.current,
    setEnabled,
  };

  return (
    <KeyboardShortcutsContext.Provider value={value}>
      {children}
    </KeyboardShortcutsContext.Provider>
  );
};
